import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { PROFILE } from "../data/content";
import { LineReveal, Rise } from "./motion/Reveal";
import { Magnetic } from "./motion/Magnetic";

const HERO_IMAGE = "https://customer-assets-rejwkqb3.emergentagent.net/job_couture-digital-1/artifacts/7ve7xztx_dffgbsdzffgsafg.webp";

export const Hero = ({ onSplineLoad, onNavigate, started }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      <div className="orb bg-violet" style={{ width: 520, height: 520, top: "-10%", left: "-8%" }} />
      <div className="orb bg-electric" style={{ width: 420, height: 420, bottom: "-6%", right: "-4%", opacity: 0.18 }} />

      {/* Hero product image (3D sling bag renders) */}
      <motion.div style={{ y, opacity }} className="absolute inset-0 z-0 flex items-center justify-center md:justify-end md:pr-[3%]">
        <img
          data-testid="hero-image"
          src={HERO_IMAGE}
          alt="Disneyland Mickey 3D sling bag, three views"
          onLoad={onSplineLoad}
          className="h-[55%] w-auto max-w-[94%] object-contain opacity-90 drop-shadow-[0_25px_70px_rgba(0,0,0,0.5)] md:h-[80%] md:max-w-[60%]"
        />
      </motion.div>

      {/* Kinetic typography overlay */}
      <motion.div style={{ y, opacity }} className="pointer-events-none relative z-10 flex min-h-screen flex-col justify-between px-5 pb-28 pt-28 md:px-12">
        <div className="flex items-start justify-between">
          <Rise delay={0.1}>
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.35em] text-electric">
              {PROFILE.name}
            </p>
          </Rise>
          <Rise delay={0.2} className="hidden max-w-[220px] text-right md:block">
            <p className="font-mono text-[0.65rem] uppercase leading-relaxed tracking-[0.2em] text-white/40">
              Digital Fashion Exhibition · Vol. 01
            </p>
          </Rise>
        </div>

        <div>
          <h1 className="font-display text-[2.6rem] font-extrabold uppercase leading-[0.86] tracking-tighter sm:text-6xl lg:text-[6.8rem]">
            <LineReveal lines={[PROFILE.heroHeadline[0]]} delay={0.1} className="block text-cream" testId="hero-line-1" />
            <LineReveal lines={[PROFILE.heroHeadline[1]]} delay={0.2} className="block stroke-text" />
            <LineReveal lines={[PROFILE.heroHeadline[2]]} delay={0.3} className="block text-cream" />
            <LineReveal lines={[PROFILE.heroHeadline[3]]} delay={0.4} className="block text-cream text-glow-magenta" />
          </h1>

          <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <Rise delay={0.9} className="max-w-md">
              <p className="font-body text-sm font-light leading-relaxed text-white/70 md:text-base">
                {PROFILE.heroSub}
              </p>
            </Rise>

            <div className="pointer-events-auto flex flex-wrap items-center gap-3">
              <Magnetic>
                <button
                  data-testid="hero-view-work"
                  onClick={() => onNavigate("work")}
                  className="group flex items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-electric"
                >
                  View Portfolio
                  <ArrowDownRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                </button>
              </Magnetic>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="pointer-events-none absolute bottom-24 left-1/2 z-10 -translate-x-1/2">
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/30">
          Scroll
        </motion.div>
      </div>
    </section>
  );
};
